/**
 * B614 leg (b) — the SHOW face's tickets step (555:2 @1440, 555:201 @390).
 * A comedy club's buyer arrives from a poster's chip or a calendar tile, so
 * the night is already chosen: the step offers the nights as chips (the
 * 14-day window), never a month grid first, and speaks the club's register —
 * a HEADLINER with the lineup, the bio, the policy badges on the photo, GOOD
 * TO KNOW from the policy facts, showtimes with their own age and seats left.
 * The derivations live in lib/showTickets.ts; composition, caps and the group
 * door are TicketsStep's own (shared helpers), so both faces sell alike.
 * Non-SHOW tenants keep TicketsStep unchanged.
 */
import type { TimeFormat } from "@transact/ui";
import { useState, type ReactElement } from "react";
import { lineupLine } from "@transact/contracts";
import { PhotoCarousel, PickerRail, PickerTile, SheetCard, SheetFoot, StickyBar, Stepper as UiStepper, Button } from "@transact/ui";
import { compositionIssues, issueHint, quantityOf, totalQuantity, type Composition } from "../lib/composition.js";
import { formatMoney } from "../lib/money.js";
import { pageDescription, pagePhotos, type ProductMedia } from "../lib/productPage.js";
import { groupParty, groupPanelTitle, stepperMax } from "../lib/groupBooking.js";
import { goodToKnowLines, nightWindow, picksWhen, policyBadges, showtimeHead, showtimeNote, showtimeWords } from "../lib/showTickets.js";
import { groupDepartures, seatsLeft, type DisplayOptions } from "./departures.js";
import { GroupBookingPanel } from "./groupBooking.js";
import { displayTotal } from "./ticketsStep.js";
import type { PoolAvailability, ProductSummary, SlotSummary } from "./screens.js";

export function ShowTicketsStep(props: {
  readonly card: ProductSummary;
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  readonly comp: Composition;
  readonly onChange: (variantId: string, quantity: number) => void;
  readonly chosenSlotId?: string;
  readonly onPickSlot: (slotId: string) => void;
  readonly selectedDayKey?: string;
  readonly onSelectDay: (key: string) => void;
  readonly onContinue: () => void;
  readonly now: number;
  readonly busy?: boolean;
  readonly locale?: string;
  readonly timeZone?: string;
  /** B621 — the tenant's clock; absent = 12h AM/PM, the ruled default. */
  readonly timeFormat?: TimeFormat;
  readonly media?: ProductMedia;
  readonly groupThreshold?: number | undefined;
  readonly businessName?: string | undefined;
  readonly onRequestGroupQuote?: (() => void) | undefined;
  /** B664 — "sheet" inside the embed dialog: 368-2's pinned foot (the number left, the door right); absent = the hosted page's sticky bar. */
  readonly foot?: "bar" | "sheet";
}): ReactElement {
  /* 555:201 — the phone's bio fold; desktop never sees the clamp or the door. */
  const [bioOpen, setBioOpen] = useState(false);
  const opts: DisplayOptions = {
    ...(props.locale !== undefined ? { locale: props.locale } : {}),
    ...(props.timeZone !== undefined ? { timeZone: props.timeZone } : {}),
    ...(props.timeFormat !== undefined ? { timeFormat: props.timeFormat } : {}),
  };
  const policies = props.card.presentation?.policies;
  const lineup = props.card.presentation?.lineup;
  const headliner = lineup?.headliner ?? props.card.name;
  const rest = lineup !== undefined ? lineupLine(lineup) : undefined;
  const photos = pagePhotos(props.card, props.media);
  const bio = pageDescription(props.card, props.media);
  const badges = policyBadges(policies);
  const know = goodToKnowLines(policies);

  const groups = groupDepartures(props.slots, opts);
  const nights = nightWindow(groups, props.now, props.selectedDayKey);
  const night = nights.find((g) => g.key === props.selectedDayKey) ?? nights[0];
  const chosen = props.slots.find((s) => s.slotId === props.chosenSlotId);

  const issues = compositionIssues(props.card, props.comp);
  const count = totalQuantity(props.comp);
  const group = props.onRequestGroupQuote !== undefined ? groupParty(props.card, props.comp, props.groupThreshold) : undefined;
  const total = displayTotal(props.card, props.comp);
  const when = picksWhen(props.card, props.comp, chosen?.start, opts);
  const missing = [...(count === 0 ? ["tickets"] : []), ...(chosen === undefined ? ["a showtime"] : [])];
  const gated = missing.length > 0 || issues.length > 0;
  const caps = props.card.variants.map((v) => v.constraints?.maxPerOrder);
  const sharedCap = caps.length > 0 && caps.every((c) => c !== undefined && c === caps[0]) ? caps[0] : undefined;
  /* B641 check-in 91 — the line under the steppers said "Up to 10 per order
     · 20 or more? That's a group." while one press past the cap opened the
     GROUP BOOKING panel saying "11 or more? That's a group." (B580: the cap
     is the groups door). The line now names the party the door opens at:
     the cap + 1 when that comes before the threshold. */
  const groupsAt =
    props.groupThreshold !== undefined && sharedCap !== undefined && sharedCap + 1 < props.groupThreshold ? sharedCap + 1 : props.groupThreshold;
  const limit = [
    ...(sharedCap !== undefined ? [`Up to ${String(sharedCap)} per order`] : []),
    ...(groupsAt !== undefined && props.onRequestGroupQuote !== undefined ? [groupPanelTitle(groupsAt)] : []),
  ].join(" · ");

  const gateLine = `Pick ${missing.join(" and ")} to continue.`;
  /* B777 (555:2) — the hosted page's line over the seated door: on the wide
     face YOUR PICKS already says what was picked, so the line says the
     house's promise (screens.tsx's sentence); the phone's floating bar keeps
     the picks (555:201). Which one shows is the stylesheet's ≥1024 rule. */
  const ready = (
    <>
      {/* 555:201 — the phone's bar: the total figure over the night */}
      <span className="show-tickets-bar-picks">
        {total !== undefined ? <span className="show-tickets-bar-total">{formatMoney(total)}</span> : null}
        {when !== undefined ? <span className="show-tickets-bar-when">{when}</span> : null}
      </span>
      <span className="show-tickets-bar-promise">The price you see is the price you pay.</span>
    </>
  );
  const door =
    group !== undefined ? (
      <Button size="lg" busy={props.busy === true} onClick={() => props.onRequestGroupQuote?.()}>
        Request a group quote
      </Button>
    ) : (
      <Button
        size="lg"
        disabled={gated}
        busy={props.busy === true}
        {...(gated && issues.length > 0 ? { describedBy: "show-tickets-issues" } : {})}
        onClick={props.onContinue}
      >
        {/* B777 (555:201) — the phone's 170 door says CONTINUE; the tail hides there */}
        {props.foot === "sheet" ? "Continue to cart" : (
          <>
            Continue<span className="show-tickets-door-tail"> to cart</span>
          </>
        )}
      </Button>
    );

  return (
    <div className={props.foot === "sheet" ? "show-tickets" : "show-tickets show-tickets-hosted"}>
      <SheetCard className="show-tickets-show" redline="show-tickets-card">
        {photos.length > 0 ? (
          <PhotoCarousel subject={headliner} photos={photos} badges={badges} redline="product-photos" />
        ) : badges.length > 0 ? (
          <ul className="show-tickets-badges">
            {badges.map((b) => (
              <li key={b}>
                <span className="show-tickets-badge">{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <header className="show-tickets-bill">
          <p className="show-tickets-eyebrow">Headliner</p>
          <h2 className="show-tickets-title">{headliner}</h2>
          {rest !== undefined ? <p className="show-tickets-lineup">{rest}</p> : null}
        </header>
        {bio !== undefined ? (
          <>
            {/* 555:201 — on a phone the bio folds to two lines behind MORE ▾
                (the fold is a 600px stylesheet rule; the door hides at
                desktop, where the frame draws the whole bio). */}
            <p className={`show-tickets-bio${bioOpen ? "" : " show-tickets-bio--folded"}`} data-redline="product-description">
              {bio}
            </p>
            <div className="show-tickets-more">
              <Button variant="textlink-accent" expanded={bioOpen} onClick={() => setBioOpen(!bioOpen)}>
                {bioOpen ? "Less ▴" : "More ▾"}
              </Button>
            </div>
          </>
        ) : null}
      </SheetCard>

      <SheetCard className="show-tickets-pick" redline="show-tickets-pick">
        {group !== undefined ? (
          <GroupBookingPanel threshold={group.size} businessName={props.businessName} cap={group.cap} />
        ) : null}
        <section aria-labelledby="show-nights-label">
          <p className="tickets-label" id="show-nights-label">
            Pick a night
          </p>
          <PickerRail describedBy="show-nights-label">
            {nights.map((g) => (
              <li key={g.key}>
                <PickerTile
                  compact
                  value={`${g.weekday} ${g.dayOfMonth}`.toUpperCase()}
                  label={g.label}
                  pressed={g.key === night?.key}
                  onPress={() => props.onSelectDay(g.key)}
                />
              </li>
            ))}
          </PickerRail>
        </section>
        {night !== undefined && night.departures[0] !== undefined ? (
          <section aria-labelledby="show-times-label">
            <p className="tickets-label" id="show-times-label">
              {showtimeHead(night.departures[0].start, opts)}
            </p>
            <PickerRail describedBy="show-times-label">
              {night.departures.map((d) => {
                const pools = props.availability[d.slotId];
                const left = pools === undefined ? undefined : seatsLeft(pools);
                const note = showtimeNote(d, policies?.minAge, pools);
                return (
                  <li key={d.slotId}>
                    <PickerTile
                      compact
                      value={showtimeWords(d.start, opts)}
                      {...(note !== undefined ? { note, ...(left !== undefined && left <= 0 ? { noteTone: "low" as const } : {}) } : {})}
                      pressed={d.slotId === props.chosenSlotId}
                      disabled={left !== undefined && left <= 0}
                      onPress={() => props.onPickSlot(d.slotId)}
                    />
                  </li>
                );
              })}
            </PickerRail>
          </section>
        ) : null}
        <section aria-labelledby="show-types-label" className="show-tickets-types">
          <p className="tickets-label" id="show-types-label">
            Tickets
          </p>
          <ul className="tickets-rows">
            {props.card.variants.map((v) => (
              <li key={v.variantId} className="tickets-row">
                <span className="tickets-row-name">
                  {v.name}
                  {v.description !== undefined ? <span className="show-tickets-row-meaning">{v.description}</span> : null}
                </span>
                {v.displayPrice !== undefined ? <span className="tickets-row-price">{formatMoney(v.displayPrice)}</span> : null}
                <UiStepper
                  frame="sheet"
                  label={v.name}
                  value={quantityOf(props.comp, v.variantId)}
                  min={0}
                  max={stepperMax(props.card, v.variantId, props.groupThreshold, count, quantityOf(props.comp, v.variantId))}
                  onChange={(next) => props.onChange(v.variantId, next)}
                  decreaseLabel={`Fewer ${v.name}`}
                  increaseLabel={`More ${v.name}`}
                />
              </li>
            ))}
          </ul>
          {limit !== "" ? <p className="tickets-footnote">{limit}</p> : null}
          {issues.length > 0 ? (
            <div id="show-tickets-issues">
              {issues.map((issue) => (
                <p key={`${issue.kind}:${issue.variantId}`} className="hint" role="status">
                  {issueHint(issue, props.card)}
                </p>
              ))}
            </div>
          ) : null}
        </section>
        <section aria-labelledby="show-picks-label" className="show-tickets-picks">
          <p className="tickets-label" id="show-picks-label">
            Your picks
          </p>
          {count === 0 ? (
            <p className="product-picks-empty">No tickets picked yet.</p>
          ) : (
            /* 555:2 — the headliner and the night left, the total as the figure right. */
            <div className="show-tickets-picked-row">
              <div className="show-tickets-picked">
                <p className="show-tickets-picked-name">{headliner}</p>
                {when !== undefined ? <p className="show-tickets-picked-when">{when}</p> : null}
              </div>
              {total !== undefined ? <p className="show-tickets-picked-total">{formatMoney(total)}</p> : null}
            </div>
          )}
        </section>
      </SheetCard>

      {know.length > 0 ? (
        <SheetCard className="show-tickets-know" redline="show-tickets-know">
          <p className="tickets-label">Good to know</p>
          <ul className="show-tickets-know-list">
            {know.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </SheetCard>
      ) : null}

      {props.foot === "sheet" ? (
        /* B664 (368-2) — the dialog's foot: the total left over what was picked, the door right. */
        <SheetFoot
          pinned
          redline="tickets-sticky"
          {...(total !== undefined && count > 0 ? { total: formatMoney(total) } : {})}
          {...(gated ? (issues.length > 0 ? {} : { sub: gateLine }) : when !== undefined ? { sub: when } : {})}
        >
          {door}
        </SheetFoot>
      ) : (
        <StickyBar
          redline="tickets-sticky"
          seatedWhenWide
          summary={gated ? (issues.length > 0 ? undefined : gateLine) : ready}
        >
          {door}
        </StickyBar>
      )}
    </div>
  );
}
